import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchConToken } from '../../../helpers/fetch';
import { openModalBottom } from '../../../actions/ui';

const DESCRIPCION_SUSCRIPCION = 'Suscripción mensual';

export const ModalPagarSuscripcion = ({ empresaId, suscripcion, isOpen, onClose, onFinishPayment, accion }) => {
  const dispatch = useDispatch();
  const { colores } = useSelector(state => state.ui);
  const { profesionalesActivos } = useSelector(state => state.suscripciones);

  const [customerToken, setCustomerToken] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [threeDSUrl, setThreeDSUrl] = useState(null);
  const [threeDSResult, setThreeDSResult] = useState(null);
  const [errorCliente, setErrorCliente] = useState(false);
  const [errorIframe, setErrorIframe] = useState(false);
  const messageListenerRef = useRef(null);
  const iframeTimeoutRef = useRef(null);

  const hideFormUI = processing || threeDSUrl || threeDSResult;

  const calcularImporteSuscripcion = () => {
    const PRECIO_BASE_PLAN = 34.90;
    const PRECIO_POR_PROFESIONAL = 8.00;
    
    const precioBase = PRECIO_BASE_PLAN;
    const precioPorProfesional = suscripcion?.precioPorProfesional || PRECIO_POR_PROFESIONAL;
    const numeroProfesionales = profesionalesActivos || 1;
    const profesionalesExtra = Math.max(0, numeroProfesionales - 1);
    
    const subtotal = precioBase + (profesionalesExtra * precioPorProfesional);
    const iva = +(subtotal * 0.21).toFixed(2);
    const totalConIva = +(subtotal + iva).toFixed(2);
    
    return totalConIva;
  };

  const IMPORTE_SUSCRIPCION = calcularImporteSuscripcion();

  useEffect(() => {
    if (!empresaId || !isOpen) {
      setCustomerToken(null);
      setIframeReady(false);
      setErrorCliente(false);
      setErrorIframe(false);
      return;
    }

    const iframeContainer = document.getElementById('paylands-iframe');
    if (iframeContainer) {
      iframeContainer.innerHTML = '';
    }
    
    setIframeReady(false);
    setErrorCliente(false);
    setErrorIframe(false);

    const createCustomer = async () => {
      try {
        const res = await fetchConToken('pasarela/crear-cliente-empresa-paylands', { empresaId }, 'POST');
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ msg: 'Error desconocido' }));
          console.error('Error del servidor al crear cliente Paylands:', res.status, errorData);
          setErrorCliente(true);
          dispatch(openModalBottom({ 
            text: [errorData.msg || 'No se pudo crear la sesión de pago. Por favor, intenta de nuevo.'], 
            timeOut: 4 
          }));
          return;
        }

        const data = await res.json();
        const token = data?.data?.Customer?.token || data?.token;
        
        if (token) {
          setCustomerToken(token);
          setErrorCliente(false);
        } else {
          setErrorCliente(true);
          dispatch(openModalBottom({ 
            text: ['No se pudo obtener el token de cliente de Paylands. Por favor, intenta de nuevo.'], 
            timeOut: 4 
          }));
        }
      } catch (err) {
        console.error('Error creando cliente en Paylands:', err);
        setErrorCliente(true);
        dispatch(openModalBottom({ 
          text: ['Error al conectar con el servicio de pagos. Por favor, intenta de nuevo más tarde.'], 
          timeOut: 4 
        }));
      }
    };

    createCustomer();
  }, [empresaId, isOpen, dispatch]);

  useEffect(() => {
    if (!customerToken || !isOpen) return;

    const iframeContainer = document.getElementById('paylands-iframe');
    if (iframeContainer) {
      iframeContainer.innerHTML = '';
    }
    
    setIframeReady(false);
    setErrorIframe(false);

    let hasInitialized = false;
    let attempts = 0;
    let checkInterval = null;

    const loadPaylands = () => {
      const container = document.getElementById('paylands-iframe');
      if (
        typeof window !== 'undefined' &&
        typeof window.paylands === 'object' &&
        container &&
        container.children.length === 0 &&
        !hasInitialized
      ) {
        hasInitialized = true;
        //window.paylands.setMode('sandbox');
        window.paylands.initializate(customerToken, 'paylands-iframe');
        return true;
      }
      return false;
    };

    const scriptExists = document.getElementById('paylands-sdk');
    if (!scriptExists) {
      const script = document.createElement('script');
      script.id = 'paylands-sdk';
      script.src = 'https://api.paylands.com/js/v1-iframe.js';
      script.async = true;
      script.onload = () => {
        setTimeout(() => {
          loadPaylands();
        }, 200);
      };
      document.body.appendChild(script);
    } else {
      checkInterval = setInterval(() => {
        if (typeof window !== 'undefined' && typeof window.paylands === 'object') {
          clearInterval(checkInterval);
          checkInterval = null;
          setTimeout(() => {
            loadPaylands();
          }, 200);
        }
      }, 100);
      
      setTimeout(() => {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
      }, 5000);
    }

    const interval = setInterval(() => {
      attempts++;
      const container = document.getElementById('paylands-iframe');
      const iframe = container?.querySelector('iframe');
      
      if (hasInitialized && iframe) {
        clearInterval(interval);
        return;
      }
      
      if (loadPaylands()) {
        clearInterval(interval);
        if (iframeTimeoutRef.current) {
          clearTimeout(iframeTimeoutRef.current);
          iframeTimeoutRef.current = null;
        }
      } else if (attempts > 30) {
        clearInterval(interval);
        setErrorIframe(true);
        dispatch(openModalBottom({ 
          text: ['El formulario de pago no se ha cargado correctamente. Por favor, cierra el modal e intenta de nuevo.'], 
          timeOut: 5 
        }));
      }
    }, 500);

    iframeTimeoutRef.current = setTimeout(() => {
      const iframe = document.querySelector('#paylands-iframe iframe');
      if (!iframe) {
        setErrorIframe(true);
        dispatch(openModalBottom({ 
          text: ['El formulario de pago está tardando en cargar. Por favor, cierra el modal e intenta de nuevo.'], 
          timeOut: 5 
        }));
      }
    }, 10000);

    const observer = new MutationObserver(() => {
      const iframe = document.querySelector('#paylands-iframe iframe');
      if (iframe) {
        setIframeReady(true);
        setErrorIframe(false);
        observer.disconnect();
        if (iframeTimeoutRef.current) {
          clearTimeout(iframeTimeoutRef.current);
          iframeTimeoutRef.current = null;
        }
      }
    });

    if (iframeContainer) {
      observer.observe(iframeContainer, { childList: true, subtree: true });
    }

    return () => {
      clearInterval(interval);
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      observer.disconnect();
      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
        iframeTimeoutRef.current = null;
      }
    };
  }, [customerToken, isOpen, dispatch]);

  useEffect(() => {
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current.listener);
    }

    const listener = async (e) => {
      const raw = e.data;
      const origin = e.origin;

      if (origin?.includes('react-devtools') || origin?.includes('chrome-extension')) {
        return;
      }

      if (typeof raw === 'string' && raw.startsWith('savedCard:')) {
        if (messageListenerRef.current?.processing) {
          return;
        }

        try {
          const parsed = JSON.parse(raw.slice('savedCard:'.length).trim());
          const source = parsed.source;
          const card_uuid = source?.uuid;

          if (!card_uuid) {
            console.warn('No se recibió uuid de tarjeta');
            return;
          }

          if (messageListenerRef.current) {
            messageListenerRef.current.processing = true;
          }
          
          setProcessing(true);
          await handlePayment(empresaId, card_uuid, source);
        } catch (error) {
          console.error('Error al procesar tarjeta y pago:', error);
          setProcessing(false);
          if (messageListenerRef.current) {
            messageListenerRef.current.processing = false;
          }
        }
      }

      if (raw === '3DS_OK' || raw === '3DS_KO') {
        setThreeDSUrl(null);
        setThreeDSResult(raw === '3DS_OK' ? 'success' : 'failure');
        setProcessing(false);
        if (messageListenerRef.current) {
          messageListenerRef.current.processing = false;
        }
        if (onFinishPayment) onFinishPayment(raw === '3DS_OK' ? 'success' : 'failure');
      }
    };

    messageListenerRef.current = { listener, processing: false };
    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
      if (messageListenerRef.current) {
        messageListenerRef.current.processing = false;
      }
    };
  }, [empresaId, onFinishPayment, accion, dispatch]);

  const handlePayment = async (empresaId, card_uuid, source) => {
    try {
      const requiredFields = ['bin', 'last4', 'expire_month', 'expire_year'];
      const missingFields = requiredFields.filter(field => !source || source[field] === undefined || source[field] === null);
      
      if (missingFields.length > 0) {
        console.error('Campos faltantes en source de Paylands:', missingFields, 'Source completo:', source);
        setProcessing(false);
        if (messageListenerRef.current) {
          messageListenerRef.current.processing = false;
        }
        dispatch(openModalBottom({
          text: ['Error al procesar los datos de la tarjeta. Por favor, intenta de nuevo.'],
          timeOut: 4
        }));
        if (onFinishPayment) {
          if (accion === 'añadir' || accion === 'cambiar') {
            onFinishPayment('card_error');
          } else {
            onFinishPayment('failure');
          }
        }
        return;
      }

      const guardarRes = await fetchConToken('pasarela/guardar-tarjeta-empresa', {
        empresa: empresaId,
        card_uuid,
        bin: source.bin,
        last4: source.last4,
        expire_month: source.expire_month,
        expire_year: source.expire_year
      }, 'POST');
      
      if (!guardarRes.ok) {
        const errorData = await guardarRes.json().catch(() => ({ msg: 'Error al guardar tarjeta' }));
        throw new Error(errorData.msg || 'Error al guardar tarjeta');
      }

      if (accion === 'añadir' || accion === 'cambiar') {
        setThreeDSResult('success');
        setProcessing(false);
        if (messageListenerRef.current) {
          messageListenerRef.current.processing = false;
        }
        if (onFinishPayment) onFinishPayment('card_saved');
        return;
      }

      const ordenRes = await fetchConToken('pasarela/generar-orden-pago-empresa', {
        empresaId,
        importe: IMPORTE_SUSCRIPCION,
        descripcion: DESCRIPCION_SUSCRIPCION
      }, 'POST');
      
      if (!ordenRes.ok) {
        const errorData = await ordenRes.json().catch(() => ({ msg: 'Error al generar la orden de pago' }));
        throw new Error(errorData.msg || 'Error al generar la orden de pago');
      }

      const ordenJson = await ordenRes.json();
      const order_uuid = ordenJson?.order_uuid;
      if (!order_uuid) throw new Error('No se pudo generar la orden de pago');

      const pagoRes = await fetchConToken('pasarela/completar-pago-empresa', {
        order_uuid,
        card_uuid,
        empresaId
      }, 'POST');
      
      if (!pagoRes.ok) {
        const errorData = await pagoRes.json().catch(() => ({ msg: 'Error al completar el pago' }));
        throw new Error(errorData.msg || 'Error al completar el pago');
      }

      const pagoJson = await pagoRes.json();

      if (pagoJson.redirectUrl?.includes('https://api.paylands.com')) {
        setThreeDSUrl(pagoJson.redirectUrl);
        return;
      }

      const result = pagoJson.ok ? 'success' : 'failure';
      setThreeDSResult(result);
      setProcessing(false);
      if (messageListenerRef.current) {
        messageListenerRef.current.processing = false;
      }
      if (onFinishPayment) onFinishPayment(result);
    } catch (err) {
      console.error('Error en el flujo de pago:', err);
      setThreeDSResult('failure');
      setProcessing(false);
      if (messageListenerRef.current) {
        messageListenerRef.current.processing = false;
      }
      dispatch(openModalBottom({
        text: [err.message || 'Error al procesar el pago. Por favor, intenta de nuevo.'],
        timeOut: 4
      }));
      if (onFinishPayment) {
        if (accion === 'añadir' || accion === 'cambiar') {
          onFinishPayment('card_error');
        } else {
          onFinishPayment('failure');
        }
      }
    }
  };

  const guardarTarjeta = () => {
    if (!customerToken) {
      dispatch(openModalBottom({ 
        text: ['No hay token de cliente disponible. Por favor, cierra el modal e intenta de nuevo.'], 
        timeOut: 4 
      }));
      return;
    }

    if (typeof window !== 'undefined' && window.paylands && iframeReady) {
      const iframeContainer = document.getElementById('paylands-iframe');
      const iframe = iframeContainer?.querySelector('iframe');
      
      if (iframe && iframe.contentWindow) {
        try {
          window.paylands.storeSourceCard(false, process.env.PAYMENT_SERVICE_SERVICE);
        } catch (error) {
          console.error('Error al guardar tarjeta con Paylands:', error);
          dispatch(openModalBottom({ 
            text: ['Error al procesar la tarjeta. Por favor, intenta de nuevo.'], 
            timeOut: 4 
          }));
        }
      } else {
        dispatch(openModalBottom({ 
          text: ['El formulario de pago no está listo. Por favor, espera a que se cargue completamente.'], 
          timeOut: 4 
        }));
      }
    } else {
      dispatch(openModalBottom({ 
        text: ['El servicio de pagos no está disponible. Por favor, intenta de nuevo más tarde.'], 
        timeOut: 4 
      }));
    }
  };

  const handleClose = () => {
    const iframeContainer = document.getElementById('paylands-iframe');
    if (iframeContainer) {
      iframeContainer.innerHTML = '';
    }
    
    setCustomerToken(null);
    setIframeReady(false);
    setProcessing(false);
    setThreeDSUrl(null);
    setThreeDSResult(null);
    setErrorCliente(false);
    setErrorIframe(false);
    if (messageListenerRef.current) {
      messageListenerRef.current.processing = false;
    }
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
      iframeTimeoutRef.current = null;
    }
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-ylm modal-pagar-suscripcion active">
      <div className="modal-bg" onClick={handleClose}></div>
      <div className="modal-wrapper" style={{ maxWidth: '400px', padding: '20px' }}>
        <div className="modal-title">
          <h3 style={{ color: colores.colorPrincipal }}>
            {accion === 'activar' ? 'Activar suscripción' : accion === 'cambiar' ? 'Cambiar tarjeta' : 'Añadir tarjeta'}
          </h3>
        </div>
        <div className="modal-body">
          <div>
            {!hideFormUI && (
              <>
                {errorCliente && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#ffebee', 
                    borderRadius: '6px', 
                    marginBottom: '15px',
                    border: '1px solid #f44336'
                  }}>
                    <p style={{ color: '#c62828', margin: 0, fontWeight: 'bold' }}>
                      Error al conectar con el servicio de pagos
                    </p>
                    <p style={{ color: '#666', margin: '5px 0 0 0', fontSize: '13px' }}>
                      No se pudo crear la sesión de pago. Por favor, cierra el modal e intenta de nuevo.
                    </p>
                  </div>
                )}
                {errorIframe && !errorCliente && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#fff3e0', 
                    borderRadius: '6px', 
                    marginBottom: '15px',
                    border: '1px solid #ff9800'
                  }}>
                    <p style={{ color: '#e65100', margin: 0, fontWeight: 'bold' }}>
                      El formulario de pago está tardando en cargar
                    </p>
                    <p style={{ color: '#666', margin: '5px 0 0 0', fontSize: '13px' }}>
                      Por favor, cierra el modal e intenta de nuevo. Si el problema persiste, verifica tu conexión a internet.
                    </p>
                  </div>
                )}
                <div id="paylands-iframe" style={{ maxHeight: 110, width: '100%', margin: '0 auto' }} />
                {!iframeReady && !errorCliente && !errorIframe && (
                  <p style={{ color: '#666', fontSize: '13px', textAlign: 'center', marginTop: '10px' }}>
                    Cargando formulario de pago...
                  </p>
                )}
              </>
            )}

            {processing && !threeDSUrl && (
              <p>Procesando el pago de tu suscripción...</p>
            )}

            {threeDSUrl && (
              <div style={{ overflow: 'hidden', minHeight: '200px' }}>
                <iframe
                  title="Autenticación 3D Secure"
                  src={threeDSUrl}
                  style={{ width: '100%', height: '100%', minHeight: 'inherit', border: 'none' }}
                  sandbox="allow-scripts allow-forms allow-same-origin"
                />
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer" style={{ marginTop: '12px', marginBottom: '27px' }}>
          <div className="buttons">
            <button
              onClick={guardarTarjeta}
              disabled={!iframeReady || processing || threeDSUrl || errorCliente || errorIframe || !customerToken}
              className="btn btn-confirm"
              style={{ backgroundColor: colores.colorPrincipal, flex: '1' }}
            >
              {accion === 'activar'
                ? 'Pagar y activar suscripción'
                : accion === 'cambiar'
                  ? 'Cambiar tarjeta'
                  : 'Añadir tarjeta'}
            </button>
            <button
              onClick={handleClose}
              className="btn btn-cancel"
              style={{ backgroundColor: '#F2F2F2', color: '#393D40', marginRight: '0', flex: '1' }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
